// assistantFly.js — FLY MODE, the assistant's sixth mode (docs/FLY_MODE.md).
// Nika 2026-10-17: "add fly mode to workspace where it runs just like fly
// keepz … replicate full fly functionality and ux also speed", "its for
// general workspace for all workspaces", "make that flow easy, beautiful,
// clean, simple".
//
// Every ask composes a PAGE, the way a Fly site does (keepz--fly): an eyebrow
// (scope · window), a title, an OPENING whose every number comes from the
// page's own data, the sections (this workspace's own widgets, records blocks
// and word blocks — the ai.boards vocabulary), the follow-up asks. The pages
// of one SESSION stand on one canvas in the main area: the newest open, the
// older ones folded to their head row; a follow-up builds on the page before.
//
// THE BRAIN is the server's fly page engine — ai.boards generate with
// `mode: 'fly'` (server AiBoardService.runFly): one fast call streams the
// head, then each section; a records section's DATA rides the same stream
// (`fly.data` → the records cache, so its card asks nothing); a short second
// call streams the opening (`fly.text`). A server without the fly shape
// answers `invalid_mode`: the page is then built from a DASHBOARD board (its
// summary is the opening, its refine asks the follow-ups) — fly mode works on
// every server.
//
// THE SESSION is an assistant thread: the first page asks with
// `threadId: 'new'` (the server makes the thread with that page), the next
// ones with its id — the thread holds every ask and page (the sidebar shows
// it; the server reads it as context). A page asked from a CALL is ephemeral
// (`opts.ephemeral`): no thread.
//
// STATE — `root._aiFly` (workspace-scoped, declared in state-app.js):
//   { v: 1, workspaceId, threadId, open, path, focus, fold, pages: [page] }
//   `fold`: the open page folded too — every page of the thread is its row
//   (assistantFlyFold; a row or a new ask opens one again). Not persisted.
//   page = { key, id, status: 'loading'|'streaming'|'ready'|'failed', phase:
//     'compose'|'data'|'write'|'done', error, prompt, eyebrow, title, opening,
//     items, followUps, fallback, at, ms }
//
// FRANK: every export is SELF-CONTAINED — no import, no module-scope constant,
// no bare call to a sibling export (frank revives each export on its own); a
// sibling is reached through `this.call('name', …)`. These run as
// `el.call('name', …)`, so `this` is an element.

// The session of the workspace on screen, or null.
export const assistantFlySession = function assistantFlySession(root) {
  const r = root && typeof root === 'object' ? root : {}
  const fly = r._aiFly
  const ws = r.workspace
  const live = (ws && (ws.id || ws._id)) || null
  if (!fly || typeof fly !== 'object') return null
  if (fly.workspaceId && live && String(fly.workspaceId) !== String(live)) return null
  return fly
}

// The canvas's own gate: the session is open, on the route it opened on (a
// navigation takes the person to that page; the session waits for the next
// ask). A page SHEET opened from a section lies OVER the canvas (the board's
// rule, functions/assistantBoard.js assistantBoardShown): closing it gives
// the page back where it was. Every key is read up front (reactive callers
// subscribe to all of them).
export const assistantFlyShown = function assistantFlyShown(root) {
  const r = root && typeof root === 'object' ? root : {}
  const path = r.currentPath
  const fly = this.call('assistantFlySession', r)
  // ONE view holds the main area: an open main thread wins, as it does over a
  // board and a deck (a `?ai=` link over an open canvas drew the same answers
  // twice — Nika 2026-10-18, "it duplicates ui").
  const fns = this.context && this.context.functions
  const thread =
    fns && typeof fns.assistantMainThreadOpen === 'function'
      ? !!this.call('assistantMainThreadOpen', r)
      : false
  // Only where the person can ask (functions/assistantFlyWorkspace.js
  // assistantFlyAvailable): Fly is the home's default view, and a workspace
  // that turned its assistant off keeps its dashboard whatever a session or a
  // link left open.
  const available =
    fns && typeof fns.assistantFlyAvailable === 'function'
      ? !!this.call('assistantFlyAvailable', r)
      : true
  if (!fly || fly.open !== true || thread || !available) return false
  const norm = (p) => {
    const raw = typeof p === 'string' && p ? p : '/'
    const s = typeof this.call === 'function' ? this.call('stripWorkspacePrefix', raw) : raw
    const v = typeof s === 'string' ? s : raw
    return v === '' ? '/' : v.length > 1 ? v.replace(/\/+$/, '') : v
  }
  return norm(fly.path) === norm(path)
}

// One page of the session by its key (or the focused one).
export const assistantFlyPage = function assistantFlyPage(root, key) {
  const fly = this.call('assistantFlySession', root)
  const pages = (fly && Array.isArray(fly.pages) ? fly.pages : []).filter(Boolean)
  const want = key || (fly && fly.focus)
  return pages.find((p) => p.key === want) || pages[pages.length - 1] || null
}

// The pages as the canvas lists them: oldest first, each with `open` (the
// focused page, unless the thread is folded; a page still being written
// stays open too).
export const assistantFlyPages = function assistantFlyPages(root) {
  const fly = this.call('assistantFlySession', root)
  const pages = (fly && Array.isArray(fly.pages) ? fly.pages : []).filter(Boolean)
  const focus = fly && fly.focus
  const folded = !!this.call('assistantFlyFolded', root)
  return pages.map((p, i) => ({
    ...p,
    index: i,
    count: pages.length,
    open: (!folded && p.key === focus) || p.status === 'loading' || p.status === 'streaming'
  }))
}

// THE THREAD FOLDED (Nika 2026-10-23: "add collapseable history when asking
// new fly question in same thread like we have in fly keepz"): the person
// folded the open page too, so every page is its row. Never while a page is
// still being written (it stays open until it is done).
export const assistantFlyFolded = function assistantFlyFolded(root) {
  const fly = this.call('assistantFlySession', root)
  if (!fly || fly.fold !== true) return false
  const pages = Array.isArray(fly.pages) ? fly.pages : []
  return !pages.some((p) => p && (p.status === 'loading' || p.status === 'streaming'))
}

// The thread's folded rows on one side of the open page ('before' | 'after'):
// the pages that are not open, in order. Folded, every page is a row before.
// A session whose focus names no page shows its newest one (assistantFlyPage),
// so the rows are read around that one — never a page twice.
export const assistantFlyRows = function assistantFlyRows(root, side) {
  const pages = this.call('assistantFlyPages', root) || []
  if (this.call('assistantFlyFolded', root)) return side === 'after' ? [] : pages
  let at = pages.findIndex((p) => p.open)
  if (at < 0) {
    const cur = this.call('assistantFlyPage', root)
    at = cur ? pages.findIndex((p) => p.key === cur.key) : -1
  }
  if (at < 0) return []
  return side === 'after'
    ? pages.filter((p, i) => i > at && !p.open)
    : pages.filter((p, i) => i < at && !p.open)
}

// Fold the open page (its head's Fold): the thread reads as its rows.
export const assistantFlyFold = function assistantFlyFold() {
  const root = this.state && this.state.root
  const fly = this.call('assistantFlySession', root)
  if (!fly || fly.fold === true || !(fly.pages || []).length) return false
  root.update({ _aiFly: { ...fly, fold: true } }, { preventFetch: true })
  return true
}

// How many sections a page draws: its items through the same validation as
// its cards (assistantFlyCells → boardItemsFor), so the thread's "N sections"
// never names a card the page does not show (ai-r1, nino + priya, dev).
export const assistantFlySectionCount = function assistantFlySectionCount(page, root) {
  const items = (page && Array.isArray(page.items) ? page.items : []).filter(Boolean)
  if (!items.length) return 0
  const res = this.call('validateBoardSpec', { items }, this.call('boardVocabularyFor', root))
  return res && Array.isArray(res.items) ? res.items.length : 0
}

// A folded row's quiet line: "14:05 · 6 sections" (what is known of it).
export const assistantFlyRowMeta = function assistantFlyRowMeta(page) {
  const p = page && typeof page.parse === 'function' ? page.parse() : page
  if (!p || typeof p !== 'object') return ''
  const parts = []
  if (p.status === 'ready' && p.at) {
    parts.push(
      String(
        this.call('localizedDate', new Date(p.at), { hour: 'numeric', minute: '2-digit' }) || ''
      )
    )
  }
  const n =
    p.status === 'ready'
      ? this.call('assistantFlySectionCount', p, this.state && this.state.root)
      : 0
  if (n > 0) {
    parts.push(
      String(
        this.call(
          'polyglot',
          n === 1 ? 'workspace.assistantFly.sectionCountOne' : 'workspace.assistantFly.sectionCount'
        )
      ).replace('{n}', String(n))
    )
  }
  return parts.filter(Boolean).join(' · ')
}

// ── The stream reducer ──────────────────────────────────────────────────────
// One page × one frame → the next page (no I/O). The frames of the fly page
// engine (server AiBoardService.runFly) — and, for the fallback, the board's
// (a board's summary is the opening, its refine asks the follow-ups).
// A server error code → the reason a page failed, so the member reads why and
// what to do (ai-r1, persona nino: "it failed with no reason"). An unknown
// code stays the general 'failed'.
export const assistantFlyErrorReason = function assistantFlyErrorReason(code) {
  const c = String(code || '')
  if (/timeout|timed out/i.test(c)) return 'timeout'
  if (/empty_page|empty_board|no_items/i.test(c)) return 'empty'
  if (/credits|ai_cap/i.test(c)) return 'credits'
  if (/model_invalid_output|ai_failed|invalid_block|invalid_spec/i.test(c)) return 'model'
  if (/ai_unavailable/i.test(c)) return 'busy'
  if (/unauthorized|forbidden/i.test(c)) return 'access'
  if (/^aborted$/i.test(c)) return 'stopped'
  return 'failed'
}

export const applyFlyFrame = function applyFlyFrame(page, frame) {
  if (!page || !frame || typeof frame !== 'object') return page
  const event = String(frame.event || '')
  const d = frame.data && typeof frame.data === 'object' ? frame.data : {}
  const line = (v) =>
    String(typeof v === 'string' ? v : '')
      .replace(/\s+/g, ' ')
      .trim()
  const status = page.status === 'loading' ? 'streaming' : page.status
  // A next ask { label, prompt } — or the quick tour { label, action: 'tour' }
  // the server offers a shell that runs it (functions/assistantFlyFollow.js).
  const follow = (list) =>
    (Array.isArray(list) ? list : [])
      .filter((f) => f && f.label && (f.prompt || f.action === 'tour'))
      .slice(0, 4)
  if (event === 'board.error') {
    const error = this.call('assistantFlyErrorReason', d.code)
    return { ...page, status: 'failed', phase: 'done', error }
  }
  if (event === 'board.start') {
    return { ...page, status, id: d.id || page.id }
  }
  if (event === 'board.head') {
    return {
      ...page,
      status,
      eyebrow: line(d.eyebrow) || page.eyebrow || '',
      title: line(d.title) || page.title || '',
      // A board (the fallback) has a summary: it is the page's opening.
      opening: page.opening || (page.fallback ? line(d.summary) : '')
    }
  }
  if (event === 'board.item') {
    const item = d.item && typeof d.item === 'object' ? d.item : null
    if (!item) return page
    const items = Array.isArray(page.items) ? page.items.slice() : []
    const at = Number.isInteger(d.index) && d.index >= 0 ? d.index : items.length
    items[at] = item
    return { ...page, status, items: Array.from(items, (x) => x || null) }
  }
  if (event === 'board.drop') {
    return { ...page, status, dropped: (Number(page.dropped) || 0) + 1 }
  }
  if (event === 'fly.data') {
    return { ...page, status, phase: page.phase === 'write' ? 'write' : 'data' }
  }
  // THE READS (Fly train 2, server ai-boards flyReads.js): the page model's
  // plan right after its head, then each read as it settles. A UI section
  // says what it is reading until its read lands (functions/assistantFlyUi.js
  // assistantFlyUiNote), then what came back (`fly.ui`). The phase stays —
  // the sections are still streaming while the reads run.
  if (event === 'fly.plan') {
    const list = Array.isArray(d.reads) ? d.reads : []
    const reads = list
      .filter((r) => r && typeof r.id === 'string' && r.id)
      .slice(0, 8)
      .map((r) => ({
        id: r.id,
        tool: typeof r.tool === 'string' ? r.tool : '',
        state: 'reading'
      }))
    return { ...page, status, reads }
  }
  if (event === 'fly.read') {
    const readId = typeof d.readId === 'string' ? d.readId : ''
    if (!readId) return page
    const reads = Array.isArray(page.reads) ? page.reads.slice() : []
    const at = reads.findIndex((r) => r && r.id === readId)
    const was = at >= 0 ? reads[at] : null
    const next = {
      id: readId,
      tool: typeof d.tool === 'string' && d.tool ? d.tool : (was && was.tool) || '',
      state: d.ok ? 'ok' : 'failed',
      ...(d.ok ? {} : { code: String(d.code || 'read_failed') })
    }
    if (at >= 0) reads[at] = next
    else reads.push(next)
    return { ...page, status, reads }
  }
  if (event === 'fly.ui') {
    const itemId = typeof d.itemId === 'string' ? d.itemId : ''
    const items = Array.isArray(page.items) ? page.items : []
    const at = itemId ? items.findIndex((it) => it && it.id === itemId) : -1
    const ui =
      d.ui &&
      typeof d.ui === 'object' &&
      (d.ui.tool === 'render_cards' || d.ui.tool === 'render_answer_ui') &&
      d.ui.args &&
      typeof d.ui.args === 'object'
        ? { tool: d.ui.tool, args: d.ui.args }
        : null
    const shown = ui
      ? { ui }
      : d.empty === true
        ? { empty: true }
        : d.error && typeof d.error === 'object'
          ? {
              error: {
                code: String(d.error.code || 'read_failed'),
                status: Number(d.error.status) || null
              }
            }
          : null
    // A result for a section this page never saw (a cut answer's) waits for
    // board.done, which carries every section with what it shows.
    if (at < 0 || !shown) return page
    const next = items.slice()
    const base = { ...items[at] }
    delete base.ui
    delete base.empty
    delete base.error
    next[at] = { ...base, ...shown }
    return { ...page, status, items: next }
  }
  if (event === 'fly.follow') {
    return { ...page, status, followUps: follow(d.followUps) }
  }
  if (event === 'fly.text') {
    const delta = typeof d.delta === 'string' ? d.delta : ''
    if (!delta) return page
    const opening = page.opening ? page.opening + (/^\s/.test(delta) ? '' : ' ') + delta : delta
    return { ...page, status, phase: 'write', opening: opening.replace(/\s+/g, ' ').trim() }
  }
  if (event === 'board.done') {
    const spec = d.spec && typeof d.spec === 'object' ? d.spec : null
    if (!spec) return { ...page, status: 'failed', phase: 'done', error: 'failed' }
    const fly = spec.kind === 'fly'
    const items = Array.isArray(spec.items) ? spec.items.slice() : page.items || []
    const followUps = fly
      ? Array.isArray(spec.followUps)
        ? spec.followUps
        : []
      : Array.isArray(spec.refine)
        ? spec.refine
        : []
    const opening = fly ? line(spec.opening) || line(spec.lead) : line(spec.summary)
    if (!items.length && !opening) {
      return { ...page, status: 'failed', phase: 'done', error: 'empty' }
    }
    // What the page read and how each read went (`spec.reads` — never the
    // rows); the plan the stream sent when the done has none.
    const reads = Array.isArray(spec.reads)
      ? spec.reads
          .filter((r) => r && typeof r.id === 'string' && r.id)
          .slice(0, 8)
          .map((r) => ({
            id: r.id,
            tool: typeof r.tool === 'string' ? r.tool : '',
            state: r.ok ? 'ok' : 'failed',
            ...(r.ok ? {} : { code: String(r.code || 'read_failed') })
          }))
      : Array.isArray(page.reads)
        ? page.reads
        : []
    return {
      ...page,
      status: 'ready',
      phase: 'done',
      error: null,
      id: spec.id || page.id,
      reads,
      eyebrow: fly ? line(spec.eyebrow) || page.eyebrow || '' : page.eyebrow || '',
      title: line(spec.title) || page.title || page.prompt,
      opening: opening || page.opening || '',
      items,
      followUps: follow(followUps),
      // A CHANGE the ask asks for (create, send, schedule …): the page shows
      // what it touches; the assistant's Tools do it (assistantFlyHandoff).
      action: fly ? line(spec.action) : '',
      // …and the app's OWN FORM that makes that change, when the page names
      // one (server ai-boards normalizeFlyForm: `{ form: '<app>.<id>', app,
      // values }`): the page opens it, prefilled, through the answer's form
      // path (assistantFlyFormOpen → assistantFormOpen) — no assistant turn.
      form:
        fly &&
        spec.form &&
        typeof spec.form === 'object' &&
        typeof spec.form.form === 'string' &&
        spec.form.form.trim()
          ? {
              form: spec.form.form.trim(),
              app: typeof spec.form.app === 'string' ? spec.form.app : '',
              values:
                spec.form.values &&
                typeof spec.form.values === 'object' &&
                !Array.isArray(spec.form.values)
                  ? { ...spec.form.values }
                  : {}
            }
          : null,
      fallback: !fly || !!page.fallback,
      ms: Number(d.ms) || (page.at ? Date.now() - page.at : null),
      model: d.model || null
    }
  }
  return page
}

// The page's cells for the canvas grid: its sections as board rows
// (functions/assistantBoard.js boardItemsFor — the same client validation, the
// same card bodies), then — while the page is still being composed — one
// skeleton per section that may still come, at its final size (CLS 0).
export const assistantFlyCells = function assistantFlyCells(page, root, cols) {
  if (!page) return []
  const n = Math.max(1, Math.floor(Number(cols)) || 1)
  const items = (Array.isArray(page.items) ? page.items : []).filter(Boolean)
  let rows = []
  if (items.length) {
    const derived = this.call(
      'boardItemsFor',
      {
        v: 1,
        id: page.id || null,
        prompt: page.prompt || '',
        title: page.title || page.prompt || '',
        summary: '',
        items,
        refine: [],
        sources: []
      },
      root
    )
    // Each cell's key carries its page: a new page's sections are NEW cards
    // (they enter, their bodies mount), never an earlier page's reused.
    // A UI section also carries how its read stands (`readState`: reading ·
    // ok · failed, the page's `reads`) and whether the page is done
    // (`settled`) — what it says until its result is in
    // (functions/assistantFlyUi.js).
    const reads = Array.isArray(page.reads) ? page.reads : []
    const settled = page.status === 'ready' || page.status === 'failed'
    // A records section's bars drill into THIS page once it is done — the
    // person's own page; a call's is the call's (assistantFlyDrill).
    const drillsInto = page.status === 'ready' && page.source !== 'call' ? page.key : null
    rows = ((derived && derived.items) || []).map((it) => {
      const read = it.kind === 'ui' ? reads.find((r) => r && r.id === it.read) : null
      return {
        ...it,
        key: page.key + '-' + it.key,
        cols: n,
        rev: 0,
        page: page.key,
        onLayer: !!page.onLayer,
        ...(it.kind === 'ui' ? { readState: read ? read.state : null, settled } : {}),
        ...(it.kind === 'records' ? { drillsInto } : {})
      }
    })
  }
  const composing =
    (page.status === 'loading' || page.status === 'streaming') &&
    (page.phase || 'compose') === 'compose'
  if (!composing) return rows
  const SIZES = ['xl', 'm', 'm']
  const want = Math.max(rows.length + 1, 3)
  const cells = rows.slice()
  for (let i = rows.length; i < want; i++) {
    cells.push({
      key: page.key + '-sk' + i,
      kind: 'skeleton',
      index: i,
      size: SIZES[i] || 'm',
      cols: n,
      rev: 0,
      onLayer: !!page.onLayer
    })
  }
  return cells
}

// A page's own box: the width of its sections' grid (the metrics of the
// view that holds it), so its head, its opening and its next asks line up
// with the cards on every host — the home (where it equals --home-grid-w),
// the page layer over a route, a call's stage.
// A page's sections: the home grid's cells, SQUARE at every step. A page is
// read in sections that FILL their cell (a chart, a KPI grid); the home's
// content-tall rows of one and two columns collapse such a body to its header
// (dev, 390 px, 2026-10-18: Keepz's figures card was 74 px tall over 201 px
// of content).
export const assistantFlyGridStyle = function assistantFlyGridStyle(s) {
  const m = this.call('assistantBoardMetrics', s)
  return this.call('assistantBoardGridStyle', {
    cols: m.cols,
    gutter: m.gutter,
    cell: m.cell,
    square: true
  })
}

// …and, beside the conversation rail (`start`, assistantFlyMeasure), it keeps
// the dashboard's LEFT edge — a centred page would run under the rail.
export const assistantFlyPageStyle = function assistantFlyPageStyle(s) {
  const m = this.call('assistantBoardMetrics', s)
  const cols = Number(m && m.cols) || 0
  const cell = Number(m && m.cell) || 0
  const gutter = Number(m && m.gutter) || 0
  if (!(cols > 0 && cell > 0)) return {}
  const width = { maxWidth: cols * cell + (cols - 1) * gutter + 'px' }
  return s && s.start === true ? { ...width, marginLeft: '0px' } : width
}

// THE FLY VIEW'S GRID (Nika 2026-10-22: "make fly same width as dashboard").
// The view is as wide as the dashboard's grid (`--home-grid-w`), and a page
// lays its sections on the dashboard's OWN grid — its columns, its cells, its
// gutter, read from the same container the board's CSS reads (`[data-home-
// board]`; the view's own width on a host without it — the page layer over a
// route), so a section is a dashboard card's size. While the conversation
// rail stands at the right edge the page keeps the dashboard's left edge and
// ends a gutter before the rail: the columns that fit there, nearest to the
// dashboard's cell, the cells stretched or shrunk to fill it — never past the
// dashboard's cap (`start`: the page is left-aligned). → { cols, gutter,
// cell, width, square, start } or null before the view has a box.
export const assistantFlyMeasure = function assistantFlyMeasure(el) {
  const host = el || this
  const node = host && host.node
  if (!node || typeof node.getBoundingClientRect !== 'function') return null
  const box = node.getBoundingClientRect()
  if (!(box.width > 0)) return null
  const board = typeof node.closest === 'function' ? node.closest('[data-home-board]') : null
  const outer = board && typeof board.getBoundingClientRect === 'function' ? board : null
  const full = this.call('homeGridMetrics', outer ? outer.getBoundingClientRect().width : box.width)
  if (!full || !(full.cell > 0)) return null
  const railEl = host.AssistantFlyRail && host.AssistantFlyRail.node
  const rail =
    railEl && typeof railEl.getBoundingClientRect === 'function'
      ? railEl.getBoundingClientRect()
      : null
  if (!rail || !(rail.width > 0) || rail.left >= box.right) return { ...full, start: false }
  const gap = full.gutter
  const avail = Math.min(box.width, rail.left - gap - box.left)
  if (!(avail > 0)) return { ...full, start: false }
  const cap = this.call('homeGridMetrics', Number.MAX_SAFE_INTEGER).cell
  const cols = Math.max(1, Math.round((avail + gap) / (full.cell + gap)))
  const cell = Math.max(1, Math.min(cap, Math.floor((avail - (cols - 1) * gap) / cols)))
  return {
    cols,
    gutter: gap,
    cell,
    width: cols * cell + (cols - 1) * gap,
    square: full.square,
    start: true
  }
}

// Read the view's grid into its OWN state (geometry of one element): once it
// is attached, on every window resize, and whenever the rail shows or leaves
// (components/AssistantFlyView.js). A view with no box keeps the last reading.
export const assistantFlySyncGrid = function assistantFlySyncGrid(el, s) {
  const host = el || this
  const m = this.call('assistantFlyMeasure', host)
  if (!m || !(m.cell > 0) || !s || typeof s.update !== 'function') return false
  const square = m.square !== false
  const start = m.start === true
  if (
    s.cols === m.cols &&
    s.cell === m.cell &&
    s.gutter === m.gutter &&
    s.square === square &&
    s.start === start
  )
    return false
  s.update({ cols: m.cols, gutter: m.gutter, cell: m.cell, square, start })
  return true
}

// A SECTION'S CELL: the board card's span (assistantBoardCardStyle) until
// the section's content is known to be EMPTY (assistantBoardCardEmpty — a
// view or widget body that says `empty`, a records card with nothing to
// show). Then the cell keeps its columns and takes ONE row: the least the
// square grid has, and it holds the head and the one sentence at every step
// (Fly polish 2026-10-18: Keepz's empty week was one line over a blank 2 × 2
// square). While the content is not known (a skeleton, a read in flight, a
// new body) the cell keeps the size it has, so nothing flashes; content that
// arrives later gives the full size back. `el` is the card; it keeps the
// last answer it knew (`__flyEmpty`). A generated board and the home keep
// their own sizes.
export const assistantFlyCardStyle = function assistantFlyCardStyle(el, s) {
  const card = el || this
  const ds = card && card.context ? card.context.designSystem : null
  const style = this.call('assistantBoardCardStyle', s, ds)
  const empty = this.call('assistantBoardCardEmpty', card, s)
  if (card && (empty === true || empty === false)) card.__flyEmpty = empty
  return card && card.__flyEmpty === true ? { ...style, gridRow: 'span 1' } : style
}

// The one quiet line under a page's title while it is being made, and its
// failure sentence after: a polyglot KEY ('' when there is nothing to say).
export const assistantFlyStatusKey = function assistantFlyStatusKey(page) {
  if (!page) return ''
  if (page.status === 'failed') {
    const e = page.error
    return e === 'timeout'
      ? 'workspace.assistantFly.failedTimeout'
      : e === 'unavailable'
        ? 'workspace.assistantFly.unavailable'
        : e === 'empty'
          ? 'workspace.assistantFly.failedEmpty'
          : e === 'credits'
            ? 'workspace.assistantFly.failedCredits'
            : e === 'stopped'
              ? 'workspace.assistantFly.stopped'
              : e === 'model'
                ? 'workspace.assistantFly.failedModel'
                : e === 'busy'
                  ? 'workspace.assistantFly.failedBusy'
                  : e === 'access'
                    ? 'workspace.assistantFly.failedAccess'
                    : 'workspace.assistantFly.failed'
  }
  if (page.status !== 'loading' && page.status !== 'streaming') return ''
  const phase = page.phase || 'compose'
  return phase === 'write'
    ? 'workspace.assistantFly.statusWrite'
    : phase === 'data'
      ? 'workspace.assistantFly.statusData'
      : 'workspace.assistantFly.statusCompose'
}

// THE RAIL — the session's conversation at the right (keepz--fly's chat
// history): shown with the canvas once it holds a page, never under a page
// sheet (the sheet is the top layer and the rail would sit half under it). An
// empty session has no history to show — its start screen stands alone
// (functions/assistantFlyStart.js). Wide screens only — the component's own
// media rule; below it the folded rows are the history.
export const assistantFlyRailShown = function assistantFlyRailShown(root) {
  const r = root && typeof root === 'object' ? root : {}
  const slot = !!(r._canvasSlot || r._canvasSlotSecond)
  const shown = !!this.call('assistantFlyShown', r)
  const hidden = !!this.call('assistantFlyRailHidden', r)
  const fly = this.call('assistantFlySession', r)
  const pages = fly && Array.isArray(fly.pages) ? fly.pages.filter(Boolean) : []
  return shown && !slot && pages.length > 0 && !hidden
}

// THE RAIL CAN BE PUT AWAY (Nika 2026-10-22: "make fly sidebar toggleable,
// show lines when its hidden"). A person's choice, the same in every
// workspace: `root._aiFlyRailHidden` (declared in state-app.js, unscoped),
// kept on this device (`symbols_fly_rail_hidden`). The root key wins once
// set; before that the device's last choice.
export const assistantFlyRailHidden = function assistantFlyRailHidden(root) {
  const r = root && typeof root === 'object' ? root : {}
  const set = r._aiFlyRailHidden
  if (set === true || set === false) return set
  try {
    return typeof localStorage !== 'undefined'
      ? localStorage.getItem('symbols_fly_rail_hidden') === '1'
      : false
  } catch (_) {
    return false
  }
}

// Hide (true) or show (false) the rail; no argument flips it. Returns the new
// value.
export const assistantFlyRailToggle = function assistantFlyRailToggle(hidden) {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return null
  const next =
    hidden === true || hidden === false ? hidden : !this.call('assistantFlyRailHidden', root)
  root.update({ _aiFlyRailHidden: next }, { preventFetch: true })
  try {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('symbols_fly_rail_hidden', next ? '1' : '0')
  } catch (_) {
    /* the choice still holds for this page */
  }
  return next
}

// THE LINES — while the rail is put away, the session stays at the edge as
// one short line per page (the open page's the longest, a page still being
// made breathing): a line opens its page, the button above them brings the
// rail back. Same gate as the rail, the other way round.
export const assistantFlyRailMarksShown = function assistantFlyRailMarksShown(root) {
  const r = root && typeof root === 'object' ? root : {}
  const slot = !!(r._canvasSlot || r._canvasSlotSecond)
  const shown = !!this.call('assistantFlyShown', r)
  const hidden = !!this.call('assistantFlyRailHidden', r)
  const fly = this.call('assistantFlySession', r)
  const pages = fly && Array.isArray(fly.pages) ? fly.pages.filter(Boolean) : []
  return shown && !slot && pages.length > 0 && hidden
}

// The lines' rows, oldest first (the rail's order). Every key the line reads
// is set: a childrenAs-'state' child falls back to ROOT state for a missing
// one.
export const assistantFlyRailMarks = function assistantFlyRailMarks(root) {
  const fly = this.call('assistantFlySession', root)
  const pages = (fly && Array.isArray(fly.pages) ? fly.pages : []).filter(Boolean)
  const focus = (fly && fly.focus) || (pages.length ? pages[pages.length - 1].key : null)
  return pages.map((p) => ({
    key: 'mark' + String(p.key || '').replace(/[^A-Za-z0-9]+/g, ''),
    pageKey: p.key || '',
    title: String(p.title || p.prompt || ''),
    open: p.key === focus,
    busy: p.status === 'loading' || p.status === 'streaming',
    failed: p.status === 'failed'
  }))
}

// The rail's lines: per page the ask (a user row), the answer (an assistant
// row — the page's opening as it streams, its failure sentence, or the
// thinking dots while it is made) and a foot (the way back to the page, its
// size and its time). The rows render in the workspace's own ChatMessage, so
// EVERY key it reads is set here: a childrenAs-'state' row falls back to ROOT
// state for a key it lacks.
export const assistantFlyRailLines = function assistantFlyRailLines(root) {
  const fly = this.call('assistantFlySession', root)
  const pages = (fly && Array.isArray(fly.pages) ? fly.pages : []).filter(Boolean)
  const open = pages.find((p) => p.key === (fly && fly.focus)) || pages[pages.length - 1] || null
  const line = (over) => ({
    kind: 'message',
    role: 'user',
    text: '',
    streaming: false,
    ts: null,
    id: null,
    agentName: null,
    toolName: null,
    tools: [],
    cards: [],
    suggestions: [],
    sources: [],
    picked: null,
    metadata: null,
    level: null,
    confidence: null,
    usage: null,
    label: null,
    prompt: null,
    factor: null,
    _grouped: false,
    _last: false,
    _tick: 0,
    pageKey: null,
    open: false,
    failed: false,
    sections: 0,
    took: '',
    ...over
  })
  const out = []
  for (const p of pages) {
    const busy = p.status === 'loading' || p.status === 'streaming'
    const failed = p.status === 'failed'
    const said = failed
      ? String(this.call('polyglot', this.call('assistantFlyStatusKey', p)) || '')
      : String(p.opening || '')
    const ms = Number(p.ms)
    // The ask — with the parts of the screen it was about, echoed under it the
    // way a chat turn's sent line is (ChatMessage `picked`). A page assembled
    // with Add section was never asked: no ask line (it drew an empty bubble).
    if (p.prompt) {
      out.push(
        line({
          key: 'q-' + p.key,
          id: 'q-' + p.key,
          text: p.prompt,
          ts: p.at || null,
          picked: Array.isArray(p.picked) && p.picked.length ? p.picked : null
        })
      )
    }
    out.push(
      line({
        key: 'a-' + p.key,
        id: 'a-' + p.key,
        role: 'assistant',
        text: said,
        streaming: busy,
        ts: p.at || null,
        agentName: p.agentName || null,
        pageKey: p.key
      })
    )
    out.push(
      line({
        key: 'f-' + p.key,
        kind: 'foot',
        role: 'foot',
        pageKey: p.key,
        open: !!open && open.key === p.key,
        failed,
        sections: busy || failed ? 0 : this.call('assistantFlySectionCount', p, root),
        took: !busy && ms > 0 ? (ms / 1000).toFixed(1) : ''
      })
    )
  }
  return out
}

// The foot's quiet line: "6 sections · 4.2 s" (what is known of it).
export const assistantFlyRailMeta = function assistantFlyRailMeta(line) {
  if (!line) return ''
  const parts = []
  const n = Number(line.sections) || 0
  if (n > 0) {
    parts.push(
      String(
        this.call(
          'polyglot',
          n === 1 ? 'workspace.assistantFly.sectionCountOne' : 'workspace.assistantFly.sectionCount'
        )
      ).replace('{n}', String(n))
    )
  }
  if (line.took) {
    parts.push(
      String(this.call('polyglot', 'workspace.assistantFly.took')).replace('{s}', line.took)
    )
  }
  return parts.join(' · ')
}

// "Composed by the AI · 2 min ago" (or the agent a call mentioned).
export const assistantFlyByline = function assistantFlyByline(page) {
  if (!page) return ''
  // A page the person assembled with Add section (functions/
  // assistantFlyWorkspace.js, key `fwl-…`) was never composed by the AI: its
  // line says what it holds instead.
  const manual = typeof page.key === 'string' && /^fwl-/.test(page.key)
  const n = manual ? this.call('assistantFlySectionCount', page, this.state && this.state.root) : 0
  const by = manual
    ? String(
        this.call(
          'polyglot',
          n === 1 ? 'workspace.assistantFly.sectionCountOne' : 'workspace.assistantFly.sectionCount'
        )
      ).replace('{n}', String(n))
    : page.agentName
      ? String(this.call('polyglot', 'workspace.assistantFly.composedByAgent')).replace(
          '{name}',
          page.agentName
        )
      : String(this.call('polyglot', 'workspace.assistantFly.composedBy'))
  // A call's page another participant asked: who asked it.
  const asked = page.askedBy
    ? ' · ' +
      String(this.call('polyglot', 'workspace.assistantFly.askedBy')).replace(
        '{name}',
        page.askedBy
      )
    : ''
  const at = Number(page.at) || 0
  const ago = at ? Math.max(0, Date.now() - at) : 0
  const when =
    ago < 60000
      ? this.call('polyglot', 'workspace.assistantFly.justNow')
      : ago < 3600000
        ? String(this.call('polyglot', 'workspace.assistantFly.minutesAgo')).replace(
            '{n}',
            String(Math.round(ago / 60000))
          )
        : this.call('localizedDate', new Date(at), { hour: 'numeric', minute: '2-digit' })
  return by + asked + ' · ' + when
}

// ── The records sections: data from the stream ─────────────────────────────
// A records section's card reads its answer from the per-workspace records
// cache (`root._aiRecords`, functions/assistantRecords.js). The fly stream
// carries that answer (`fly.data`), so the section is MARKED as loading the
// moment it lands (its card then asks nothing) and FILLED when its data comes.
// `entry`: 'mark' | { answer } (the server's answer) | { entry } (a cache
// entry as another participant's call shared it, already normalized) |
// { error }. The key is the card's own (recordsBlockOf → recordsBlockNormalize
// → boardSpecHash).
export const assistantFlyRecordsWrite = function assistantFlyRecordsWrite(root, item, entry) {
  if (!root || typeof root.update !== 'function' || !item) return false
  const block = this.call('recordsBlockNormalize', item)
  if (!block) return false
  const ws = root.workspace
  const wsId = ws && (ws.id || ws._id) ? String(ws.id || ws._id) : ''
  const key = wsId + ':' + this.call('boardSpecHash', block)
  let lang = ''
  try {
    lang = String(this.call('getActiveLang') || '')
  } catch (_) {
    lang = ''
  }
  const bag = root._aiRecords && typeof root._aiRecords === 'object' ? root._aiRecords : {}
  const have = bag[key]
  let next
  if (entry === 'mark') {
    // Never over an answer the cache already holds (the same block on an
    // earlier page): the card shows it at once.
    if (have && have.status !== 'failed') return false
    next = { status: 'loading', fly: true, at: Date.now(), ...(lang ? { lang } : {}) }
  } else if (entry && entry.entry && typeof entry.entry === 'object') {
    const status = ['ready', 'empty'].includes(entry.entry.status) ? entry.entry.status : 'failed'
    next = {
      status,
      answer: entry.entry.answer || null,
      at: Date.now(),
      shared: true,
      ...(lang ? { lang } : {})
    }
  } else if (entry && entry.answer) {
    next = {
      ...this.call('assistantRecordsAnswer', entry.answer, block),
      at: Date.now(),
      ...(lang ? { lang } : {})
    }
  } else {
    // The server refused the read: the card says so and offers Try again.
    if (have && have.status !== 'loading') return false
    next = { status: 'failed', at: Date.now(), ...(lang ? { lang } : {}) }
  }
  const keys = Object.keys(bag).filter((k) => k !== key)
  const kept = {}
  for (const k of keys.slice(-39)) kept[k] = bag[k]
  kept[key] = next
  root.update({ _aiRecords: kept }, { preventFetch: true })
  return true
}

// After a page: a section still marked (its data never came — a fallback
// board, a cut stream) is read the ordinary way, so no card waits forever.
export const assistantFlyRecordsSettle = function assistantFlyRecordsSettle(root, items) {
  if (!root || typeof root.update !== 'function') return 0
  const ws = root.workspace
  const wsId = ws && (ws.id || ws._id) ? String(ws.id || ws._id) : ''
  let lang = ''
  try {
    lang = String(this.call('getActiveLang') || '')
  } catch (_) {
    lang = ''
  }
  let n = 0
  for (const item of Array.isArray(items) ? items : []) {
    if (!item || item.kind !== 'records') continue
    const block = this.call('recordsBlockNormalize', item)
    if (!block) continue
    const key = wsId + ':' + this.call('boardSpecHash', block)
    const have = root._aiRecords && root._aiRecords[key]
    if (!have || have.status !== 'loading' || !have.fly) continue
    n += 1
    const land = (entry) => {
      const cur = root._aiRecords && root._aiRecords[key]
      if (!cur || cur.status !== 'loading' || !cur.fly) return
      root.update(
        {
          _aiRecords: {
            ...root._aiRecords,
            [key]: { ...entry, at: Date.now(), ...(lang ? { lang } : {}) }
          }
        },
        { preventFetch: true }
      )
    }
    Promise.resolve()
      .then(() => this.call('assistantRecordsFetch', block, wsId || null, lang || null))
      .then(
        (entry) => land(entry && entry.status ? entry : { status: 'failed' }),
        () => land({ status: 'failed' })
      )
  }
  return n
}

// ── The flow ────────────────────────────────────────────────────────────────
// The ask → a page in the SAME tick (the canvas opens with the new page's
// frame before any network work), then the stream fills it. Called as
// `(prompt, opts)` or `({ prompt, attachments, attached })` (the composer's
// send, functions/homeAiSend.js). opts: { ephemeral (a call: no thread),
// agentName (the agent a call mentioned), timeoutMs, onFrame (a call's
// broadcaster, functions/meetFly.js), aiScope (the scope a home tile asks
// about — it replaces the composer's; assistantFlyAsk) }.
export const assistantFlyGenerate = async function assistantFlyGenerate(prompt, opts) {
  const el = this
  const root = el && el.state && el.state.root
  const input = prompt && typeof prompt === 'object' ? prompt : { prompt }
  const text = String(input.prompt == null ? '' : input.prompt)
    .replace(/\s+/g, ' ')
    .trim()
  if (!root || typeof root.update !== 'function' || !text) return null
  const o = { ...(opts || {}) }
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  const fns = el.context && el.context.functions
  const madeOn = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  const key = 'fp' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
  // The first ask after a load on a route whose canvas has not mounted yet
  // (the page layer mounts with its first page): the stored session comes
  // back first, so the new page joins it — never a fresh session whose save
  // would overwrite the stored pages (dev /crm, 2026-10-18).
  if (!o.ephemeral && !el.call('assistantFlySession', root)) {
    try {
      el.call('assistantFlyRestore', root)
    } catch (_) {
      /* no stored session: a new one starts */
    }
  }
  const session = el.call('assistantFlySession', root) || {
    v: 1,
    workspaceId,
    threadId: null,
    open: false,
    path: madeOn,
    focus: null,
    pages: []
  }
  // The session's thread: its id, or 'new' for the first page — never twice
  // (a second ask while the first is still making the thread stays off it).
  const pending = (session.pages || []).some(
    (p) => p && p.thread === 'new' && p.status !== 'ready' && p.status !== 'failed'
  )
  const thread = o.ephemeral ? null : session.threadId ? session.threadId : pending ? null : 'new'
  // A server without the fly shape answers `invalid_mode` once: the session
  // remembers it (in memory only — never stored, so a server that gains the
  // shape is asked again after a reload) and makes its next pages from a board
  // at once, without the extra round trip. A call's BOARD ask (meet
  // functions/meetFly.js, `engine: 'board'`) is a page made from a board too.
  const boardOnly = session.engine === 'board' || o.engine === 'board'
  // A SOLO ask's context (Fly train 2, functions/assistantFlyContext.js): the
  // composer's scope with the subjects the page on screen shows, and the parts
  // of the screen the member pointed at — read NOW, while the page the member
  // asked from is still the one on screen. A CALL's ask carries none: its page
  // goes to every member of the call, and the reads and the regions are the
  // asker's own (the coordinator's answer, 2026-10-19).
  let asked = null
  if (!o.ephemeral) {
    try {
      asked = el.call('assistantFlyAskContext', root)
    } catch (_) {
      asked = null
    }
  }
  // A home tile pinned to a built app asks about THAT app: its scope replaces
  // the composer's (the home's: the whole workspace).
  const tileScope = o.aiScope && typeof o.aiScope === 'object' && o.aiScope.kind ? o.aiScope : null
  const askContext = tileScope
    ? { ...((asked && asked.context) || {}), aiScope: tileScope }
    : asked && asked.context
      ? asked.context
      : null
  const askPicked = asked && Array.isArray(asked.picked) ? asked.picked : []
  // The project (and board page) the ask was made on — its "Do it" sends it
  // (assistantFlyHandoff; functions/assistantFlyContext.js
  // assistantFlyAskTarget, with the WHY). A call's page has none.
  let target = null
  if (!o.ephemeral) {
    try {
      target = el.call('assistantFlyAskTarget', root, input.attached)
    } catch (_) {
      target = null
    }
  }
  const page = {
    key,
    id: null,
    status: 'loading',
    phase: 'compose',
    error: null,
    prompt: text,
    eyebrow: '',
    title: '',
    opening: '',
    items: [],
    followUps: [],
    reads: [],
    fallback: boardOnly,
    thread,
    at: Date.now(),
    ms: null,
    ...(o.agentName ? { agentName: String(o.agentName).slice(0, 60) } : {}),
    ...(target ? { target } : {}),
    // The parts of the screen the ask was about: the rail echoes them under
    // the ask the way a chat turn's sent line does (ChatMessage `picked`).
    ...(askPicked.length
      ? {
          picked: askPicked.map((p) => ({
            keyPath: Array.isArray(p.keyPath) ? p.keyPath.slice(0, 4) : [],
            key: typeof p.key === 'string' ? p.key : '',
            tag: typeof p.tag === 'string' ? p.tag : 'region',
            pickedAt: p.pickedAt || null,
            inspect: {
              title: String((p.inspect && p.inspect.title) || p.title || '').slice(0, 120)
            }
          }))
        }
      : {})
  }
  // THE PAGE IS PLACED AFTER THE REQUEST LEAVES (dev Keepz, 2026-10-17: the
  // canvas render took ~0.4–1.2 s of main thread, and it ran BEFORE the
  // request went out): the model works while the page renders. A frame that
  // comes before the page is placed waits for it. ONE view holds the main
  // area: a board, a deck or the thread steps aside then too.
  let placed = !!o.ephemeral
  const queued = []
  // An ephemeral page (a call) lives in its caller's hands, not the session.
  let local = page
  const read = () => {
    if (o.ephemeral) return local
    const fly = el.call('assistantFlySession', root)
    return fly && Array.isArray(fly.pages)
      ? fly.pages.find((p) => p && p.key === key) || null
      : null
  }
  const write = (next) => {
    if (!next) return
    if (o.ephemeral) {
      local = next
    } else {
      const fly = el.call('assistantFlySession', root)
      if (!fly || !Array.isArray(fly.pages) || !fly.pages.some((p) => p && p.key === key)) return
      root.update(
        { _aiFly: { ...fly, pages: fly.pages.map((p) => (p && p.key === key ? next : p)) } },
        { preventFetch: true }
      )
    }
    if (typeof o.onFrame === 'function') {
      try {
        o.onFrame(next)
      } catch (_) {
        /* the broadcaster's own failure never breaks the page */
      }
    }
  }
  const apply = (frame) => {
    if (!placed) {
      queued.push(frame)
      return
    }
    const cur = read()
    if (!cur || cur.status === 'failed') return
    const ev = frame && frame.event
    const d = frame && frame.data
    // Records sections: their data comes in this stream.
    if (ev === 'board.item' && d && d.item && d.item.kind === 'records') {
      el.call('assistantFlyRecordsWrite', root, d.item, 'mark')
    }
    if (ev === 'fly.data' && d) {
      const item = (cur.items || []).find((it) => it && it.id === d.itemId)
      if (item)
        el.call(
          'assistantFlyRecordsWrite',
          root,
          item,
          d.answer ? { answer: d.answer } : { error: d.error || true }
        )
    }
    write(el.call('applyFlyFrame', cur, frame))
  }
  const place = () => {
    if (placed) return
    if (fns && typeof fns.assistantBoardDismiss === 'function') el.call('assistantBoardDismiss')
    if (fns && typeof fns.assistantDeckDismiss === 'function') el.call('assistantDeckDismiss')
    if (fns && typeof fns.assistantMainThreadClose === 'function')
      el.call('assistantMainThreadClose')
    if (fns && typeof fns.meetFlyTheater === 'function') el.call('meetFlyTheater', false)
    const now = el.call('assistantFlySession', root) || session
    const before = (now.pages || []).filter(Boolean)
    // Ask again: the new page takes the old one's place.
    const swap = o.replace && before.some((p) => p.key === o.replace)
    // The picks the ask took leave the composer with the same write, as a
    // sent chat turn's do (functions/_simone/sendPrompt.js): the page and the
    // rail carry them from here.
    const taken = new Set(askPicked)
    const picks = Array.isArray(root.pickedElements) ? root.pickedElements : null
    root.update(
      {
        _aiFly: {
          ...now,
          workspaceId,
          open: true,
          path: madeOn,
          focus: key,
          fold: false,
          pages: (swap
            ? before.map((p) => (p.key === o.replace ? page : p))
            : [...before, page]
          ).slice(-20)
        },
        ...(taken.size && picks ? { pickedElements: picks.filter((p) => !taken.has(p)) } : {})
      },
      { preventFetch: true }
    )
    placed = true
    for (const f of queued.splice(0)) apply(f)
  }
  const sdk = el.sdk || (typeof el.call === 'function' ? el.call('getSDK') : null)
  const timeoutMs = Number(o.timeoutMs) > 0 ? Number(o.timeoutMs) : 60000
  const reasonOf = (err) => {
    const msg = String((err && (err.code || err.message)) || err || '')
    if (
      /unknown entity|does not support op|is not available|not found on service|unknown method/i.test(
        msg
      )
    ) {
      return 'unavailable'
    }
    return el.call('assistantFlyErrorReason', msg)
  }
  let capabilities = null
  try {
    capabilities = el.call('assistantClientCapabilities', sdk)
  } catch (_) {
    capabilities = null
  }
  // THE BUILT-IN VIEWS a page may place (calendar, tasks, files …): the
  // shell's providers' declarations, the list a chat turn sends as
  // `context.appCatalog` (functions/assistantAppViews.js). Installed apps'
  // views the server reads from their manifests itself.
  let catalog = null
  try {
    catalog = el.call('assistantAppCatalog')
  } catch (_) {
    catalog = null
  }
  // A page the person stopped (assistantFlyStop) keeps what arrived: its
  // request ends at the next frame and nothing after it lands on the page.
  const stopped = () => {
    const cur = read()
    return !!(cur && cur.status === 'failed' && cur.error === 'stopped')
  }
  const run = async (mode) => {
    let timer = null
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null
    try {
      if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
      // A SOLO fly ask draws UI sections (`capabilities.ui`, server
      // recordsBlock clientRendersUi): the page may then SHOW its reads with
      // the chat's answer-UI components (functions/assistantFlyUi.js). A
      // CALL's ask never does — its reads run under the asker's access and
      // the call shows the page to every member; it keeps the sections it
      // has today (the coordinator's answer, 2026-10-19).
      const solo = mode === 'fly' && !o.ephemeral
      // …nor offers the quick tour: one member's tour on everyone's page.
      const shared = capabilities ? { ...capabilities } : null
      if (shared) delete shared.tour
      const caps = solo
        ? { ...(capabilities || {}), ui: 1 }
        : shared && Object.keys(shared).length
          ? shared
          : null
      const pendingCall = sdk.execute('ai.boards', 'generate', {
        prompt: text,
        mode,
        ...(mode === 'fly' && thread ? { threadId: thread } : {}),
        ...(mode === 'fly' && Array.isArray(catalog) && catalog.length
          ? { appCatalog: catalog }
          : {}),
        ...(workspaceId ? { workspaceId } : {}),
        ...(input.attached ? { attached: input.attached } : {}),
        ...(input.attachments ? { attachments: input.attachments } : {}),
        ...(caps ? { capabilities: caps } : {}),
        // What "this" means (the chat turn's own `context.aiScope` and
        // `context.inspected`; server AiBoardController pageContextOf). An
        // SDK that does not forward `context` yet drops it — the page is
        // then made as before.
        ...(solo && askContext ? { context: askContext } : {}),
        // The SDK's streamed board request takes the signal to its fetch.
        ...(ctrl ? { signal: ctrl.signal } : {}),
        onEvent: (frame) => {
          if (stopped()) {
            if (ctrl) {
              try {
                ctrl.abort()
              } catch (_) {
                /* already ended */
              }
            }
            return
          }
          if (frame && typeof frame === 'object' && frame.event !== 'board.done') apply(frame)
        }
      })
      const res = await Promise.race([
        Promise.resolve(pendingCall),
        new Promise((resolve, reject) => {
          timer = setTimeout(() => reject(new Error('timeout')), timeoutMs)
        })
      ])
      const data = res && typeof res === 'object' ? res.data || res : null
      apply({ event: 'board.done', data: data && data.spec ? data : { spec: data } })
      return data
    } finally {
      if (timer) clearTimeout(timer)
    }
  }
  // The request leaves now (the shell's capabilities answer at once after
  // their first read); the page is placed in the next task, after every
  // microtask of the SDK's request ran.
  const first = Promise.resolve(capabilities)
    .catch(() => null)
    .then((c) => {
      capabilities = c && typeof c === 'object' ? c : null
      return run(boardOnly ? 'dashboard' : 'fly')
    })
  first.catch(() => {})
  if (!placed) {
    await new Promise((resolve) => setTimeout(resolve, 0))
    place()
  }
  let done = null
  try {
    done = await first
  } catch (err) {
    const msg = String((err && (err.code || err.message)) || err || '')
    if (stopped()) {
      // The person stopped it: the aborted request's error is no failure of
      // the page, and a stopped page is never asked again as a board.
    } else if (!boardOnly && /invalid_mode/i.test(msg)) {
      // A server without the fly shape: the same page, built from a board.
      const fly = el.call('assistantFlySession', root)
      if (fly && fly.engine !== 'board') {
        root.update({ _aiFly: { ...fly, engine: 'board' } }, { preventFetch: true })
      }
      const cur = read()
      if (cur) write({ ...cur, status: 'streaming', fallback: true })
      try {
        done = await run('dashboard')
      } catch (err2) {
        const cur2 = read()
        if (cur2 && !stopped()) {
          write({ ...cur2, status: 'failed', phase: 'done', error: reasonOf(err2) })
        }
      }
    } else {
      const cur = read()
      if (cur) write({ ...cur, status: 'failed', phase: 'done', error: reasonOf(err) })
    }
  }
  const finished = read()
  if (finished) el.call('assistantFlyRecordsSettle', root, finished.items)
  // The session learns its thread from the first page; the sidebar's history
  // list hears of it.
  const threadId = done && typeof done.threadId === 'string' ? done.threadId : null
  if (!o.ephemeral) {
    const fly = el.call('assistantFlySession', root)
    if (fly && threadId && fly.threadId !== threadId) {
      root.update({ _aiFly: { ...fly, threadId } }, { preventFetch: true })
      try {
        if (fns && typeof fns.refreshSessionsAfterTurn === 'function')
          el.call('refreshSessionsAfterTurn', 'general')
      } catch (_) {
        /* the list refreshes on its own next read */
      }
    }
    el.call('assistantFlyPersist', root)
  }
  return read()
}

// STOP — the page being made ends where it is (Nika 2026-10-23: "fly voice and
// ai should stop when I talk, add interrupting"). The voice talk loop calls it
// when the person speaks over a page still in the making (shared
// components/VoiceOverlay.js, the barge-in): the page keeps what arrived and
// says it was stopped (assistantFlyStatusKey), its request ends at its next
// frame (assistantFlyGenerate aborts its own stream), and a late answer never
// lands on it. `key`: one page; without it, every page of the session still
// being made. Returns how many pages stopped.
export const assistantFlyStop = function assistantFlyStop(key) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return 0
  const fly = el.call('assistantFlySession', root)
  if (!fly || !Array.isArray(fly.pages)) return 0
  let n = 0
  const pages = fly.pages.map((p) => {
    if (!p || (key && p.key !== key)) return p
    if (p.status !== 'loading' && p.status !== 'streaming') return p
    n += 1
    return { ...p, status: 'failed', phase: 'done', error: 'stopped' }
  })
  if (n) root.update({ _aiFly: { ...fly, pages } }, { preventFetch: true })
  return n
}

// A follow-up chip, Ask again, a shared link: a new page of the session. A
// home tile pinned to a built app (functions/assistantFlyStart.js
// assistantFlyStartAsk) brings that app's scope: `{ prompt, aiScope }`.
export const assistantFlyAsk = function assistantFlyAsk(entry) {
  const p = entry && typeof entry === 'object' ? entry.prompt : entry
  const text = String(p == null ? '' : p).trim()
  if (!text) return null
  const scope = entry && typeof entry === 'object' ? entry.aiScope : null
  if (scope && typeof scope === 'object' && scope.kind) {
    return this.call('assistantFlyGenerate', text, { aiScope: scope })
  }
  return this.call('assistantFlyGenerate', text)
}

// A page's own controls (Ask again, a next ask, Share, Save) — on the canvas
// or on a call's stage: `s` is the page's state (or a next-ask chip's, which
// carries where its page lives). A call's ask stays in the call (its agent).
export const assistantFlyPageAct = function assistantFlyPageAct(s, verb, arg) {
  const page = s && typeof s.parse === 'function' ? s.parse() : s
  if (!page || typeof page !== 'object') return null
  if (verb === 'ask') {
    const prompt = String((arg && arg.prompt) || page.prompt || '').trim()
    if (!prompt) return null
    if (page.source === 'call') {
      return this.call('meetFlyAsk', {
        prompt,
        agentKey: page.agentKey || null,
        agentName: page.agentName || null
      })
    }
    return this.call('assistantFlyAsk', prompt)
  }
  // Ask again / Try again: the SAME page made again, in its place (a call's
  // stage shows one page anyway).
  if (verb === 'again') {
    const prompt = String(page.prompt || '').trim()
    if (!prompt) return null
    if (page.source === 'call') {
      return this.call('meetFlyAsk', {
        prompt,
        agentKey: page.agentKey || null,
        agentName: page.agentName || null
      })
    }
    return this.call('assistantFlyGenerate', prompt, { replace: page.key })
  }
  // A folded row: its page opens where it lives (the session, or the call's
  // theater).
  if (verb === 'focus') {
    return page.source === 'call'
      ? this.call('meetFlyFocus', page.key)
      : this.call('assistantFlyFocus', page.key)
  }
  if (verb === 'share') return this.call('assistantFlyShare', page)
  if (verb === 'save') return this.call('assistantFlySave', page)
  return null
}

// A page that asks for a CHANGE (the engine's `action`): the assistant does
// it in TOOLS mode for this one ask — its own confirm cards, nothing written
// before the member confirms — and the person's mode stays Fly. A call's page
// never hands off (the asker would act for everyone on the call).
// The page's TARGET (the project its ask was made on) rides the turn as its
// structured attachment (homeAiSend's 4th slot, `attached.canvas`): the
// action words often do not name it (dev 2026-10-31, "Add page QA Test One"
// on Velvet Lantern became a Helmet SEO page — functions/
// assistantFlyContext.js assistantFlyAskTarget). A page with no target sends
// none, as before.
export const assistantFlyHandoff = function assistantFlyHandoff(s) {
  const page = s && typeof s.parse === 'function' ? s.parse() : s
  if (!page || typeof page !== 'object' || page.source === 'call') return false
  const text = String(page.action || '').trim()
  if (!text) return false
  const t = page.target && typeof page.target === 'object' ? page.target.canvas : null
  const attached =
    t && typeof t === 'object' && t.projectId
      ? {
          canvas: {
            projectId: String(t.projectId),
            ...(t.boardPageId ? { boardPageId: String(t.boardPageId) } : {}),
            ...(t.title ? { title: String(t.title) } : {}),
            nodes: [],
            connections: []
          }
        }
      : null
  this.call('homeAiSend', text, null, 0, attached, { mode: 'tools' })
  return true
}

// A page whose change an app's OWN FORM makes (boards-r1, BLD-26 — marcus
// "Add a contact": the CRM manifest ships forms[] crm-contact, yet "Do it" ran
// a paid Tools turn). The page's `form` becomes the answer's form card
// (functions/assistantAppForms.js — the same matching, access rule and
// opening), so the page opens the app's form, prefilled, and nothing is
// written until the member saves there. null — the page names no form.
export const assistantFlyFormCard = function assistantFlyFormCard(s) {
  const page = s && typeof s.parse === 'function' ? s.parse() : s
  const f = page && typeof page === 'object' ? page.form : null
  if (!f || typeof f !== 'object' || typeof f.form !== 'string' || !f.form) return null
  if (page.source === 'call') return null
  return {
    type: 'form',
    form: f.form,
    values: f.values && typeof f.values === 'object' ? f.values : {}
  }
}

// 'ready' | 'denied' | 'refused' | 'loading' | null (no form on the page).
export const assistantFlyFormStatus = function assistantFlyFormStatus(s) {
  const card = this.call('assistantFlyFormCard', s)
  if (!card) return null
  const fns = (this.context && this.context.functions) || {}
  if (typeof fns.assistantFormStatus !== 'function') return 'refused'
  return this.call('assistantFormStatus', card) || 'refused'
}

// Open it. → the request id, or null (not ready).
export const assistantFlyFormOpen = function assistantFlyFormOpen(s) {
  const card = this.call('assistantFlyFormCard', s)
  if (!card || this.call('assistantFlyFormStatus', s) !== 'ready') return null
  return this.call('assistantFormOpen', card)
}

// The plain line a page says when it names a form the workspace cannot open
// (the app is not installed, or the member may not use it) — never a dead
// button, never a blank form panel. '' otherwise.
export const assistantFlyFormNote = function assistantFlyFormNote(s) {
  const status = this.call('assistantFlyFormStatus', s)
  if (status === 'denied')
    return String(this.call('polyglot', 'workspace.assistantBlock.formDenied'))
  if (status === 'refused')
    return String(this.call('polyglot', 'workspace.assistantBlock.formGone'))
  return ''
}

// ── A section's own controls (components/AssistantFlyView.js AssistantFlyCard)
// THE ONE SQUARE BUTTON is the home card's (functions/homeWidgetFrame.js
// homeWidgetActionOf): a widget's own VERB when it declares one — "+ New deal"
// opens the deal form, "Open follow-ups" the follow-ups view — else ↗ Open
// when the section has somewhere to go (a view's page, a widget's or a
// records card's app), else none. A page is read-only, so never a Pin.
export const assistantFlyCardVerb = function assistantFlyCardVerb(s) {
  const own = s && s.kind === 'widget' ? s.action || (s.spec && s.spec.action) : null
  return !!(own && (own.call || own.route || own.set))
}

// The square button in words: the verb's label, or "Open {app}" — `{app}` takes
// the section's title (twenty ↗ never share one accessible name).
export const assistantFlyCardActionLabel = function assistantFlyCardActionLabel(s) {
  const a = s && s.kind !== 'skeleton' ? this.call('homeWidgetActionOf', s) : null
  if (!a) return null
  const name = s.title ? String(this.call('polyglot', s.title)) : ''
  return String(this.call('polyglot', a.label)).replace('{app}', name)
}

// Run it: the verb on the MOUNTED body, so the app's own function registry
// resolves it, and a form opens in the shell's form host (homeWidgetRunAction);
// else ↗ Open (assistantBoardCardOpen: a view's page with the filters it
// resolved, else the section's route). `this` is the clicked element.
export const assistantFlyCardAct = function assistantFlyCardAct(s, event) {
  if (!this.call('assistantFlyCardVerb', s)) return this.call('assistantBoardCardOpen', s, event)
  const host = typeof this.lookup === 'function' ? this.lookup('CardBody') : null
  const body = host && host.content ? host.content : null
  return this.call('homeWidgetRunAction', s, event, body)
}

// What a section's close does: 'drill' — a drill section on the person's own
// done page closes, its source stays (assistantFlyDrillClose); 'remove' — a
// hand-made page's section leaves it (functions/assistantFlyWorkspace.js
// assistantFlyWorkspaceRemove); null — no close.
export const assistantFlyCardCloses = function assistantFlyCardCloses(s) {
  if (!s || typeof s.page !== 'string' || !s.page) return null
  if (s.kind === 'records' && s.drill && typeof s.drill === 'object') {
    return s.drillsInto === s.page ? 'drill' : null
  }
  return s.page.startsWith('fwl-') ? 'remove' : null
}

export const assistantFlyCardCloseLabel = function assistantFlyCardCloseLabel(s) {
  const what = this.call('assistantFlyCardCloses', s)
  if (what === 'drill') {
    return String(this.call('polyglot', 'workspace.assistantRecords.drillClose')).replace(
      '{title}',
      (s && s.title) || ''
    )
  }
  return what === 'remove' ? String(this.call('polyglot', 'shell.flyWorkspace.remove')) : null
}

export const assistantFlyCardClose = function assistantFlyCardClose(s) {
  const what = this.call('assistantFlyCardCloses', s)
  if (what === 'drill') return this.call('assistantFlyDrillClose', s.page, s.key)
  if (what === 'remove') return this.call('assistantFlyWorkspaceRemove', s.page, s.key)
  return false
}

// D2 DRILL-DOWN ON A FLY PAGE (functions/assistantBoard.js assistantBoardDrill,
// over the page instead of the board): a bar of a records chart → a LIST
// section of that one value, RIGHT AFTER its source section. The section is a
// real records item of the page's own spec, so it derives, reads, saves and
// shares like every other section. One drill per source: a new value
// replaces the last. Only the person's own page once it is done (a call's
// page is the call's), never one being saved, never past 12 sections.
// `cellKey` is the source's cell key (`<page>-<item key>`,
// assistantFlyCells). → true when the section joined.
export const assistantFlyDrill = function assistantFlyDrill(pageKey, cellKey, bar) {
  const MAX_ITEMS = 12
  const root = this.state && this.state.root
  const fly = this.call('assistantFlySession', root)
  if (!root || typeof root.update !== 'function' || !fly) return false
  const pages = Array.isArray(fly.pages) ? fly.pages : []
  const page = pages.find((p) => p && p.key === pageKey)
  if (!page || page.status !== 'ready' || page.source === 'call' || page.save === 'saving') {
    return false
  }
  if (!Array.isArray(page.items) || typeof cellKey !== 'string') return false
  const prefix = page.key + '-'
  if (!cellKey.startsWith(prefix)) return false
  const sourceKey = cellKey.slice(prefix.length)
  const derived = this.call('boardItemsFor', { v: 1, items: page.items }, root)
  const source = ((derived && derived.items) || []).find(
    (it) => it && it.key === sourceKey && it.kind === 'records'
  )
  if (!source || !source.records) return false
  const entry = this.call('assistantRecordsEntry', root, source.records)
  const made = this.call('assistantRecordsDrillOf', source.records, entry, bar)
  if (!made) return false
  const keyOf = (it) => {
    // A records item as validation reads one: `kind: 'records'`, or no kind
    // with a collection (functions/assistantBoard.js validateBoardSpec).
    if (!it || typeof it !== 'object' || (it.kind && it.kind !== 'records')) return ''
    const n = this.call('recordsBlockNormalize', it)
    return n ? 'r:' + this.call('boardSpecHash', n) : ''
  }
  const kept = page.items.filter((it) => !(it && it.drill && it.drill.from === sourceKey))
  if (kept.length >= MAX_ITEMS) return false
  const at = kept.findIndex((it) => keyOf(it) === sourceKey)
  if (at < 0) return false
  const items = kept.slice()
  items.splice(at + 1, 0, { ...made.block, drill: { from: sourceKey, ...made.drill } })
  root.update(
    {
      _aiFly: {
        ...fly,
        pages: pages.map((p) => (p === page ? { ...page, items, save: null } : p))
      }
    },
    { preventFetch: true }
  )
  this.call('assistantFlyPersist', root)
  return true
}

// A drill section's own close on a Fly page: that section leaves the page (its
// source stays). A section that is not a drill is never removed this way.
// `cellKey` is the drill section's cell key.
export const assistantFlyDrillClose = function assistantFlyDrillClose(pageKey, cellKey) {
  const root = this.state && this.state.root
  const fly = this.call('assistantFlySession', root)
  if (!root || typeof root.update !== 'function' || !fly) return false
  const pages = Array.isArray(fly.pages) ? fly.pages : []
  const page = pages.find((p) => p && p.key === pageKey)
  if (!page || !Array.isArray(page.items) || page.save === 'saving') return false
  if (typeof cellKey !== 'string' || !cellKey.startsWith(page.key + '-')) return false
  const key = cellKey.slice(page.key.length + 1)
  const keyOf = (it) => {
    // A records item as validation reads one: `kind: 'records'`, or no kind
    // with a collection (functions/assistantBoard.js validateBoardSpec).
    if (!it || typeof it !== 'object' || (it.kind && it.kind !== 'records')) return ''
    const n = this.call('recordsBlockNormalize', it)
    return n ? 'r:' + this.call('boardSpecHash', n) : ''
  }
  const items = page.items.filter((it) => !(it && it.drill && keyOf(it) === key))
  if (items.length === page.items.length) return false
  root.update(
    {
      _aiFly: {
        ...fly,
        pages: pages.map((p) => (p === page ? { ...page, items, save: null } : p))
      }
    },
    { preventFetch: true }
  )
  this.call('assistantFlyPersist', root)
  return true
}

// Open one page of the session (the others fold).
export const assistantFlyFocus = function assistantFlyFocus(key) {
  const root = this.state && this.state.root
  const fly = this.call('assistantFlySession', root)
  if (!fly || !key || (fly.focus === key && fly.fold !== true)) return false
  root.update({ _aiFly: { ...fly, focus: key, fold: false, open: true } }, { preventFetch: true })
  return true
}

// Close the canvas (Esc, the close button, a navigation of the person's own):
// the session stays — the next fly ask brings it back with its pages.
export const assistantFlyClose = function assistantFlyClose(opts) {
  const root = this.state && this.state.root
  const fly = this.call('assistantFlySession', root)
  if (!fly || !fly.open) return false
  root.update({ _aiFly: { ...fly, open: false } }, { preventFetch: true })
  if (opts && opts.focusComposer) this.call('assistantBoardFocus', 'composer')
  return true
}

// A NEW session: the pages leave the canvas, the next ask starts a thread.
export const assistantFlyNew = function assistantFlyNew() {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  const madeOn = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  // THE RECENT PAGES (the start screen's Recent, Nika 2026-10-23 "make fly
  // starter screen more advanced"): the pages this new session puts away,
  // newest first, before the ones it already carried — by their asks, each
  // once, at most six. A hand-made page has no ask and is not carried.
  const was = this.call('assistantFlySession', root)
  const recent = []
  const seen = new Set()
  const carry = (p) => {
    const prompt = p && typeof p.prompt === 'string' ? p.prompt.trim() : ''
    if (!prompt || seen.has(prompt.toLowerCase()) || recent.length >= 6) return
    seen.add(prompt.toLowerCase())
    recent.push({
      prompt: prompt.slice(0, 500),
      title: String((p && p.title) || prompt).slice(0, 160),
      at: Number(p && p.at) || Date.now()
    })
  }
  const outgoing = was && Array.isArray(was.pages) ? was.pages : []
  for (let i = outgoing.length - 1; i >= 0; i--) {
    if (outgoing[i] && outgoing[i].status === 'ready') carry(outgoing[i])
  }
  for (const p of was && Array.isArray(was.recent) ? was.recent : []) carry(p)
  root.update(
    {
      _aiFly: {
        v: 1,
        workspaceId,
        threadId: null,
        open: false,
        path: madeOn,
        focus: null,
        pages: [],
        recent
      }
    },
    { preventFetch: true }
  )
  this.call('assistantFlyPersist', root)
  this.call('assistantBoardFocus', 'composer')
  return true
}

// The page's link: this workspace with `?fly=<the ask>` — opening it asks the
// same question as the reader (their grants, their data), the way a Fly link
// does (keepz--fly /v/<id>?q=<ask>).
export const assistantFlyLink = function assistantFlyLink(page) {
  if (!page || !page.prompt) return ''
  const loc = typeof window !== 'undefined' && window.location ? window.location : null
  const base = loc ? String(loc.origin || '') + String(loc.pathname || '/') : '/'
  return base + '?fly=' + encodeURIComponent(String(page.prompt).slice(0, 500))
}

// Share: copy the link, one notification. `ref` = a page or its key.
export const assistantFlyShare = function assistantFlyShare(ref) {
  const root = this.state && this.state.root
  const page = ref && typeof ref === 'object' ? ref : this.call('assistantFlyPage', root, ref)
  const url = this.call('assistantFlyLink', page)
  if (!url) return false
  const say = (ok) => {
    try {
      this.call('openNotification', {
        type: ok ? 'success' : 'error',
        title: this.call(
          'polyglot',
          ok ? 'workspace.assistantFly.linkCopied' : 'workspace.share.copyFailed.title'
        ),
        message: ok ? this.call('polyglot', 'workspace.assistantFly.linkCopiedHint') : url
      })
    } catch (_) {
      /* no notification host (a test, a standalone surface) */
    }
  }
  const clip = typeof navigator !== 'undefined' && navigator.clipboard ? navigator.clipboard : null
  if (clip && typeof clip.writeText === 'function') {
    Promise.resolve(clip.writeText(url)).then(
      () => say(true),
      () => say(false)
    )
  } else say(false)
  return url
}

// Can this page be saved as a board? It holds a section a board keeps — a
// widget, a view, a records or a word block. A UI section is one read's
// result at the time of the ask (Fly train 2): a page of only those has
// nothing a board could read again.
export const assistantFlySavable = function assistantFlySavable(page) {
  // A page or its state (the page body's `s`): its items read by index.
  const items = page && page.items
  const n = items && typeof items.length === 'number' ? items.length : 0
  for (let i = 0; i < n; i++) {
    const it = items[i]
    if (it && it.kind !== 'ui') return true
  }
  return false
}

// Save: the page as a saved board (ai.boards save) — its sections, its title,
// its ask as the summary (a board reads again every time it opens: the
// opening was one run's answer and goes stale — ai-r1, nino), its follow-ups
// as the board's refine asks.
// `ref` = a page or its key; a CALL's page marks its save on its record in the
// call's pages (meet functions/meetFly.js, root.meetFly v2).
export const assistantFlySave = async function assistantFlySave(ref) {
  const root = this.state && this.state.root
  const page = ref && typeof ref === 'object' ? ref : this.call('assistantFlyPage', root, ref)
  if (!root || !page || page.status !== 'ready' || !this.call('assistantFlySavable', page)) {
    return { ok: false, reason: 'unavailable' }
  }
  const mark = (save) => {
    if (page.source === 'call') {
      const call = root.meetFly
      const pages = call && Array.isArray(call.pages) ? call.pages : []
      if (!pages.some((rec) => rec && rec.key === page.key)) return
      root.update(
        {
          meetFly: {
            ...call,
            pages: pages.map((rec) =>
              rec && rec.key === page.key ? { ...rec, page: { ...rec.page, save } } : rec
            )
          }
        },
        { preventFetch: true }
      )
      return
    }
    const fly = this.call('assistantFlySession', root)
    if (!fly) return
    root.update(
      {
        _aiFly: {
          ...fly,
          pages: fly.pages.map((p) => (p && p.key === page.key ? { ...p, save } : p))
        }
      },
      { preventFetch: true }
    )
  }
  if (page.save === 'saved' || page.save === 'saving') return { ok: page.save === 'saved' }
  mark('saving')
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  try {
    const sdk = this.sdk || this.call('getSDK')
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    const spec = {
      v: 1,
      title: page.title || page.prompt,
      summary:
        page.title && page.title !== page.prompt ? String(page.prompt || '').slice(0, 280) : '',
      // A UI section is one read's result at the time of the ask, not a live
      // section: a board keeps the sections that read again (the server's
      // board validator refuses a `ui` item).
      items: (page.items || []).filter((it) => it && it.kind !== 'ui'),
      // the text asks only: the tour chip is no board ask
      refine: (page.followUps || [])
        .filter((f) => f && f.prompt)
        .map((f) => ({ label: f.label, prompt: f.prompt })),
      sources: []
    }
    const saved = await sdk.execute('ai.boards', 'save', {
      spec,
      prompt: page.prompt,
      title: spec.title,
      ...(workspaceId ? { workspaceId } : {})
    })
    const row = saved && typeof saved === 'object' ? saved.data || saved : null
    if (row && row.id) {
      const list = Array.isArray(root._aiBoards) ? root._aiBoards : []
      root.update(
        { _aiBoards: [row, ...list.filter((x) => x && x.id !== row.id)] },
        { preventFetch: true }
      )
    }
    mark('saved')
    try {
      // the toast opens the board it names (ai-r1, priya: no way to it)
      this.call('openNotification', {
        type: 'success',
        title: this.call('polyglot', 'workspace.assistantFly.saved'),
        message: this.call('polyglot', 'workspace.assistantFly.savedHint'),
        ...(row && row.id
          ? {
              actionLabel: this.call('polyglot', 'workspace.assistantFly.openBoard'),
              action: { call: 'assistantBoardSelect', args: [row.id] }
            }
          : {})
      })
    } catch (_) {
      /* no notification host */
    }
    return { ok: true, board: row }
  } catch (err) {
    mark('failed')
    return { ok: false, reason: 'failed', error: (err && err.message) || String(err) }
  }
}

// ── Keep the session across a reload (a per-person convenience) ────────────
// The pages (never the data — a records section reads again) and the thread
// id, per workspace, in this browser. A failed write is no loss.
// A UI section (Fly train 2) keeps what its read showed — the server keeps
// the same on the thread's page (`metadata.flyPage`): the page comes back as
// it was. A store too full for every page keeps the newest ones that fit,
// whole (never a section without its result).
// A failed page keeps its row too — its ask, its reason and its time (ai-r1,
// persona nino: after a reload the failure was gone). A page still being made
// cannot finish after a reload: it comes back as stopped, its ask kept and
// what half arrived dropped (assistantFlyStop's shape).
export const assistantFlyPersist = function assistantFlyPersist(root) {
  const fly = this.call('assistantFlySession', root)
  if (!fly || !fly.workspaceId) return false
  if (typeof localStorage === 'undefined') return false
  let pages = (fly.pages || [])
    .filter((p) => p && typeof p.key === 'string')
    .slice(-12)
    .map((p) =>
      p.status === 'ready'
        ? p
        : {
            ...p,
            status: 'failed',
            error: p.status === 'failed' ? p.error || 'failed' : 'stopped',
            id: p.id || null,
            eyebrow: '',
            title: '',
            opening: '',
            items: [],
            reads: [],
            followUps: [],
            form: null
          }
    )
    .map((p) => ({
      key: p.key,
      id: p.id,
      status: p.status,
      ...(p.status === 'failed' ? { error: p.error } : {}),
      phase: 'done',
      prompt: p.prompt,
      eyebrow: p.eyebrow,
      title: p.title,
      opening: p.opening,
      items: p.items,
      reads: Array.isArray(p.reads) ? p.reads : [],
      followUps: p.followUps,
      action: p.action || '',
      form: p.form && typeof p.form === 'object' ? p.form : null,
      fallback: !!p.fallback,
      at: p.at,
      // how long it took (the rail's foot: "6 sections · 4.2 s")
      ms: Number(p.ms) > 0 ? Number(p.ms) : null,
      ...(p.agentName ? { agentName: p.agentName } : {}),
      ...(Array.isArray(p.picked) && p.picked.length ? { picked: p.picked } : {}),
      // the project its ask was made on: "Do it" after a reload still sends it
      ...(p.target && typeof p.target === 'object' ? { target: p.target } : {})
    }))
  for (;;) {
    try {
      localStorage.setItem(
        'symbols_fly_' + fly.workspaceId,
        JSON.stringify({
          v: 1,
          threadId: fly.threadId || null,
          pages,
          // the start screen's Recent (assistantFlyNew)
          recent: Array.isArray(fly.recent) ? fly.recent.slice(0, 6) : [],
          focus: fly.focus || null,
          // when it was last worked on (assistantFlyRestore brings back only a
          // recent session; an older one gives way to the start screen)
          savedAt: Date.now()
        })
      )
      return true
    } catch (_) {
      if (pages.length <= 1) return false
      pages = pages.slice(1)
    }
  }
}

// On the canvas's first mount for a workspace: the stored session comes back
// CLOSED (the next fly ask opens it with its pages) — when it was worked on in
// the last twelve hours. An older one stays in the assistant's history (its
// thread) and Fly opens on the workspace's start screen instead
// (functions/assistantFlyStart.js; Nika 2026-10-22, "give fly initial screen
// to start from"): its pages were about live data on another day. A session
// stored before `savedAt` existed is dated by its newest page.
export const assistantFlyRestore = function assistantFlyRestore(root) {
  if (!root || typeof root.update !== 'function') return false
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  if (!workspaceId || this.call('assistantFlySession', root)) return false
  let stored = null
  try {
    stored =
      typeof localStorage !== 'undefined'
        ? JSON.parse(localStorage.getItem('symbols_fly_' + workspaceId) || 'null')
        : null
  } catch (_) {
    stored = null
  }
  if (!stored || typeof stored !== 'object') return false
  const pages = (Array.isArray(stored.pages) ? stored.pages : []).filter(
    (p) => p && typeof p.key === 'string' && typeof p.prompt === 'string'
  )
  // An empty session comes back too when it carries the start screen's Recent
  // (a fresh Fly put pages away: assistantFlyNew).
  const recent = (Array.isArray(stored.recent) ? stored.recent : [])
    .filter((p) => p && typeof p.prompt === 'string' && p.prompt)
    .slice(0, 6)
  if (!pages.length && !recent.length) return false
  const FRESH_MS = 12 * 60 * 60 * 1000
  const savedAt =
    Number(stored.savedAt) ||
    Math.max(
      0,
      ...pages.map((p) => Number(p && p.at) || 0),
      ...recent.map((p) => Number(p.at) || 0)
    )
  if (!(savedAt > 0) || Date.now() - savedAt > FRESH_MS) return false
  const madeOn = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  root.update(
    {
      _aiFly: {
        v: 1,
        workspaceId,
        threadId: typeof stored.threadId === 'string' ? stored.threadId : null,
        open: false,
        path: madeOn,
        focus: pages.some((p) => p.key === stored.focus)
          ? stored.focus
          : pages.length
            ? pages[pages.length - 1].key
            : null,
        pages,
        recent
      }
    },
    { preventFetch: true }
  )
  return true
}

// A cold `?fly=<ask>` link: asked once the shell can answer, then the address
// drops it (query-only replaceState — the ?board= contract, homeBoardUrl.js).
export const assistantFlyUrlOpen = function assistantFlyUrlOpen(root) {
  const w = typeof window !== 'undefined' ? window : null
  if (!w || !w.location || !root) return false
  let url
  try {
    url = new URL(w.location.href)
  } catch (_) {
    return false
  }
  const ask = (url.searchParams.get('fly') || '').trim()
  if (!ask) return false
  url.searchParams.delete('fly')
  if (w.history && typeof w.history.replaceState === 'function') {
    // The shell's query-only address contract (functions/homeBoardUrl.js):
    // no navigation, no router dispatch, no new history entry.
    // frank-allow FA410
    w.history.replaceState(w.history.state, '', url.pathname + url.search + url.hash)
  }
  this.call('assistantFlyGenerate', ask.slice(0, 500))
  return true
}

// ── The view's mount and keyboard ───────────────────────────────────────────
// Mounted (and on a workspace switch): read the grid, ask once per workspace
// whether the board service answers here (`_aiBoardsAvailable`), bring the
// stored session back, honour a cold `?fly=` link.
export const assistantFlyMounted = function assistantFlyMounted(el, s) {
  const host = el || this
  this.call('assistantBoardGridRead', host, s, 'assistantFlySyncGrid')
  const root = s && s.root
  const ws = root && root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  if (host.__flyMountedFor === workspaceId) return false
  host.__flyMountedFor = workspaceId
  this.call('assistantFlyRestore', root)
  if (host.mainHost !== 'over') {
    const sdk = host.sdk || this.call('getSDK')
    Promise.resolve(this.call('assistantBoardProbe', root, sdk)).then(() => {
      if (host.node && host.node.isConnected !== false) host.call('assistantFlyUrlOpen', root)
    })
  }
  return true
}

// Esc closes the canvas and returns focus to the composer — one row of the
// shell keyboard registry (functions/shortcuts.js), registered when the view
// mounts and taken out when it is removed. Two views register it (the home's
// and the page layer's), each under its own id.
export const assistantFlyShortcuts = function assistantFlyShortcuts(el) {
  const view = el || this
  if (!view || typeof view.call !== 'function') return () => false
  const over = view.mainHost === 'over'
  view.call('listShortcuts')
  const shown = (h, root) => {
    const node = view.node
    if (!node || node.isConnected === false) return false
    if (root && (root._canvasSlot || root._canvasSlotSecond)) return false
    return !!view.call('assistantFlyShown', root)
  }
  const off = view.call('registerShortcut', {
    id: over ? 'assistantFlyCloseOver' : 'assistantFlyClose',
    keys: 'escape',
    label: over ? null : 'workspace.assistantFly.shortcutClose',
    group: 'shell.headerTabs.dashboard',
    groupOrder: 3,
    order: 8,
    when: shown,
    run: () => view.call('assistantFlyClose', { focusComposer: true })
  })
  const stop = () => (typeof off === 'function' ? off() : false)
  view.__flyShortcutsOff = stop
  return stop
}

// The new page's head comes to the top of the view (under the header), once,
// when it starts — never while the person reads an older page.
// The scroll follows the scroller's own CSS `scroll-behavior` (the brand reset
// turns smooth scrolling off under reduced motion), so no motion is decided
// here. The open page is the view's child keyed by the page's own key (found
// through the element tree, never a DOM query). It is the house reveal
// (functions/assistantReveal.js — Nika 2026-10-23: "when answering in new
// section always scroll top of that section"): the page can render a beat
// after the focus that made it, so the reveal waits for its node instead of
// giving up at once. A host without the house reveal scrolls directly.
export const assistantFlyReveal = function assistantFlyReveal(el, key) {
  const host = el || this
  if (!host || !key || host.__flyRevealed === key) return false
  const fns = (this && this.context && this.context.functions) || {}
  if (typeof fns.assistantRevealTop === 'function') {
    host.__flyRevealed = key
    this.call('assistantRevealTop', host, key, 'page' + key)
    return true
  }
  const page = typeof host.lookdown === 'function' ? host.lookdown(key) : null
  const node = page && page.node
  if (!node || typeof node.scrollIntoView !== 'function') return false
  host.__flyRevealed = key
  node.scrollIntoView({ block: 'start' })
  return true
}
